import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, DEFAULT_LOCALE, type Locale } from './useLanguage';
import { useStorefront } from './useStorefront';
import { rewriteForIT } from '@/lib/rewriteForIT';
import { rewriteForAL, shouldScrubForAL } from '@/lib/rewriteForAL';

// German defaults - the source of truth
export const DE_DEFAULTS: Record<string, string> = {
  // Header / Nav
  'nav.home': 'Startseite',
  'nav.blog': 'Blog',
  'nav.login': 'Anmelden',
  'nav.register': 'Registrieren',
  'nav.account': 'Mein Konto',
  'nav.logout': 'Abmelden',
  'nav.cart': 'Warenkorb',
  // Product page
  'product.add_to_cart': 'In den Warenkorb',
  'product.delivery_time': 'Lieferzeit',
  'product.reviews': 'Bewertungen',
  'product.description': 'Beschreibung',
  'product.enter_link': 'Link eingeben',
  'product.free_gift': 'Geschenk 🎁',
  'product.select_package': 'Paket wählen',
  'product.select_type': 'Typ wählen',
  'product.quantity': 'Anzahl',
  'product.from_price': 'ab',
  'product.please_select': 'Bitte auswählen',
  'product.select_type_first': 'Bitte wähle zuerst einen Typ aus.',
  'product.no_variations': 'Keine Variationen verfügbar.',
  'product.no_thanks': 'Nein, danke',
  'product.important': 'Wichtig',
  'product.profile_public': 'Profil muss öffentlich sein.',
  'product.confirm_public': 'Ich bestätige, dass mein Profil öffentlich ist.',
  'product.error_target_required': 'Bitte fülle das Feld "{label}" aus.',
  'product.error_gift_target_required': 'Bitte fülle das Geschenk-Feld aus.',
  'product.error_confirm_required': 'Bitte bestätige, dass dein Profil öffentlich ist.',
  'product.error_select_variation': 'Bitte wähle ein Paket aus.',
  'product.also_popular': 'Ebenfalls beliebt',
  'product.badge_orders': 'Bestellungen',
  'product.last_ordered': 'Zuletzt bestellt',
  'product.from_city': 'aus',
  'product.types_overview': 'Typen im Überblick',
  'product.types_overview_follower': 'Follower-Typen im Überblick',
  'product.recommended': 'Empfohlen',
  'product.switch_to_type': 'Zu {name} wechseln →',
  'product.fast_results': 'Schnelle Resultate',
  'product.within': 'Innerhalb von',
  'product.secure_payment': 'Sichere Zahlung durch SSL Schutz',
  'product.secure_payment_desc': 'Sicher bezahlen durch SSL Verschlüsselung',
  'product.best_prices': 'Günstigste Preise',
  'product.best_prices_desc': 'Die günstigsten Preise in der ganzen Schweiz',
  // Cart / Checkout
  'cart.title': 'Warenkorb',
  'cart.empty': 'Dein Warenkorb ist leer',
  'cart.checkout': 'Zur Kasse',
  'cart.subtotal': 'Zwischensumme',
  'cart.total': 'Total',
  'cart.discount_code': 'Rabattcode',
  'cart.apply': 'Einlösen',
  'cart.remove': 'Entfernen',
  'checkout.title': 'Kasse',
  'checkout.email': 'E-Mail',
  'checkout.pay_now': 'Jetzt bezahlen',
  'checkout.order_summary': 'Bestellübersicht',
  'checkout.complete_order': 'Bestellung abschliessen',
  'checkout.almost_done': 'Fast fertig! Gebe deine Daten ein, um die Bestellung zu erhalten.',
  'checkout.pay_order': 'Bestellung #{number} bezahlen',
  'checkout.pay_order_desc': 'Schliesse die Zahlung für deine bestehende Bestellung ab.',
  'checkout.billing_details': 'Rechnungsdaten',
  'checkout.first_name': 'Vorname',
  'checkout.last_name': 'Nachname',
  'checkout.address': 'Adresse',
  'checkout.postal_code': 'Postleitzahl',
  'checkout.city': 'Ort / Stadt',
  'checkout.country': 'Land',
  'checkout.payment': 'Zahlung',
  'checkout.loading_payment': 'Zahlungsoptionen werden geladen...',
  'checkout.creating_order': 'Bestellung wird erstellt...',
  'checkout.continue_payment': 'Weiter zur Zahlung',
  'checkout.order_now': 'Jetzt bestellen',
  'checkout.processing': 'Wird verarbeitet...',
  'checkout.twint_desc': 'Bezahle direkt mit TWINT',
  'checkout.stripe_label': 'Kreditkarte / Apple Pay / Klarna / PayPal',
  'checkout.processing_payment': 'Verarbeitung…',
  'checkout.your_order': 'Deine Bestellung',
  'checkout.product': 'Produkt',
  'checkout.subtotal': 'Zwischensumme',
  'checkout.discount': 'Rabatt',
  'checkout.code_discount': 'Code-Rabatt',
  'checkout.vat': 'MWST',
  'checkout.total': 'Gesamtsumme',
  'checkout.discount_code': 'Rabatt Code',
  'checkout.use_code': 'Code verwenden',
  'checkout.invalid_code': 'Dieser Rabattcode ist ungültig oder nicht aktiv.',
  'checkout.code_expired': 'Dieser Rabattcode ist abgelaufen.',
  'checkout.code_used_up': 'Dieser Rabattcode wurde bereits zu oft verwendet.',
  'checkout.code_once_per_user': 'Dieser Rabattcode kann nur einmal pro Kunde verwendet werden.',
  'checkout.code_per_user_limit': 'Dieser Rabattcode kann pro Kunde nur {n}× verwendet werden.',
  'checkout.code_email_required': 'Bitte gib zuerst deine E-Mail-Adresse ein, um diesen Code zu verwenden.',
  'checkout.min_order': 'Mindestbestellwert für diesen Code',
  'checkout.discount_applied': 'Rabatt angewendet!',
  'checkout.code_error': 'Fehler beim Überprüfen des Rabattcodes.',
  'checkout.privacy_note': 'Wir verwenden Ihre Daten gemäss unserer Datenschutzerklärung',
  'checkout.ssl_secure': 'SSL gesicherter Checkout',
  'checkout.live_support': '24/7 live support',
  'checkout.payment_methods': 'Twint / Apple Pay / Kredit-, Debitkarte / Klarna',
  'checkout.money_back': '100% money back guarantee!',
  'checkout.money_back_desc': 'Risikofrei einkaufen: Bei Nichtzufriedenheit erhalten Sie Ihr Geld zurück, garantiert!',
  'checkout.order_received': 'Bestellung eingegangen!',
  'checkout.order_number': 'Bestellnummer',
  'checkout.order_received_desc': 'Wir haben deine Bestellung erhalten und beginnen sofort mit der Bearbeitung.',
  'checkout.back_to_shop': 'Zurück zum Shop',
  'checkout.cart_empty': 'Warenkorb ist leer',
  'checkout.loading_order': 'Bestellung wird geladen…',
  'checkout.step_cart': 'Warenkorb',
  'checkout.step_checkout': 'Kasse',
  'checkout.step_complete': 'Abschluss',
  // Footer
  'footer.legal': 'Rechtliches',
  'footer.contact': 'Kontakt',
  'footer.imprint': 'Impressum',
  'footer.terms': 'AGB',
  'footer.privacy': 'Datenschutz',
  'footer.payment_methods': 'Zahlungsmethoden',
  'footer.follow_us': 'Folge uns',
  'footer.description': 'Wir sind ein Unternehmen, das Instagram- und Tiktok-Follower, -Likes und -Views verkauft. Wir haben viele Jahre Erfahrung in Social Media und vertreten keines der sozialen Netzwerke, die Gegenstand unserer Dienstleistung sind. Die Instagram-, Meta- oder TikTok-Handelsmarken, die auf unserer Website angezeigt werden, sind Eigentum von Instagram LLC, Meta Inc. bzw. Bytedance Ltd.',
  'footer.payment_desc': 'Beliebte Zahlungsarten wie Twint, Visa, Mastercard oder American Express',
  'footer.services': 'Unsere Services',
  'footer.links': 'Links',
  'footer.copyright': 'Alle Rechte vorbehalten',
  'footer.big_points': 'Big-Punkte',
  'footer.sitemap': 'Sitemap',
  'footer.status': 'Status',
  'nav.breadcrumb_home': 'Home',
  // SideCart
  'cart.target': 'Ziel',
  'cart.also_popular': 'Ebenfalls beliebt',
  'cart.add': '+ Hinzufügen',
  'cart.from': 'ab',
  'cart.customers_bought': 'Kunden haben dies gekauft',
  'cart.vat': 'MWST {rate}%',
  'cart.discount_percent': '{percent}% Rabatt',
  'cart.discount_active': '🎉 Juhuu! {percent}% Rabatt aktiviert',
  'cart.discount_remaining': '🎁 Nur noch {amount} bis {percent}% Rabatt!',
  'cart.continue_shopping': 'Weiter einkaufen',
  'cart.secure_payment': 'Sichere Bezahlung · SSL-verschlüsselt',
  // Newsletter
  'newsletter.badge': '15% Rabatt auf deine erste Bestellung',
  'newsletter.title': 'Hol dir Tipps für Social-Media-Wachstum',
  'newsletter.subtitle': 'Melde dich zum Newsletter an und erhalte exklusive Tipps, Strategien und einen 15% Rabattcode für deine erste Bestellung.',
  'newsletter.placeholder': 'Deine E-Mail-Adresse',
  'newsletter.cta': 'Jetzt anmelden',
  'newsletter.cta_loading': 'Wird gesendet…',
  'newsletter.success': 'Danke! Schau in dein Postfach – dein 15% Rabattcode ist unterwegs.',
  'newsletter.error_invalid': 'Bitte gib eine gültige E-Mail-Adresse ein.',
  'newsletter.error_generic': 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
  'newsletter.privacy': 'Kein Spam. Abmeldung jederzeit möglich.',
  // Blog sidebar
  'blog.search': 'Suche',
  'blog.search_placeholder': 'Blog durchsuchen…',
  'blog.searching': 'Suche…',
  'blog.no_results': 'Keine Ergebnisse',
  'blog.categories': 'Kategorien',
  'blog.all_posts': 'Alle Beiträge',
  'blog.related': 'Ähnliche Beiträge',
  // Reviews
  'reviews.title': 'Kundenbewertungen',
  'reviews.write': 'Bewertung schreiben',
  'reviews.name': 'Name',
  'reviews.comment': 'Kommentar',
  'reviews.submit': 'Absenden',
  'reviews.stars': 'Sterne',
  'reviews.title_count': 'Bewertungen für',
  'reviews.your_review_for': 'Deine Bewertung für',
  'reviews.rating': 'Bewertung',
  'reviews.email': 'E-Mail',
  'reviews.name_placeholder': 'Dein Name',
  'reviews.email_placeholder': 'deine@email.ch',
  'reviews.optional': 'optional',
  'reviews.comment_placeholder': 'Erzähle uns von deiner Erfahrung...',
  'reviews.sending': 'Senden...',
  'reviews.fill_required': 'Bitte Name, E-Mail und Bewertung angeben',
  'reviews.invalid_email': 'Bitte eine gültige E-Mail-Adresse eingeben',
  'reviews.error_sending': 'Fehler beim Senden',
  'reviews.submitted': 'Bewertung eingereicht! Sie wird nach Prüfung veröffentlicht.',
  'reviews.reply_from': 'Antwort von BigFollow',
  // General
  'general.loading': 'Laden…',
  'general.error': 'Ein Fehler ist aufgetreten',
  'general.back': 'Zurück',
  'general.save': 'Speichern',
  'general.cancel': 'Abbrechen',
  // Rich description sections
  'rich.intro_suffix': 'Dein Weg zu mehr Reichweite',
  'rich.benefits_title': 'Deine Vorteile auf einen Blick',
  'rich.how_it_works_title': 'So funktioniert\'s – In 3 einfachen Schritten',
  'rich.for_whom_title': 'Für wen lohnt es sich?',
  'rich.why_bigfollow_title': 'Warum BigFollow?',
  'rich.tips_title': 'Tipps für maximale Ergebnisse',
  'rich.faq_title': 'Häufig gestellte Fragen',
  'general.search': 'Suchen',
  'general.close': 'Schliessen',
  // How it works
  'how_it_works.title': 'So funktioniert\'s',
  'how_it_works.step1_title': 'Produkt wählen',
  'how_it_works.step1_desc': 'Wähle das gewünschte Social-Media-Paket aus',
  'how_it_works.step2_title': 'Link eingeben',
  'how_it_works.step2_desc': 'Gib deinen Benutzernamen oder Link ein',
  'how_it_works.step3_title': 'Bezahlen',
  'how_it_works.step3_desc': 'Bezahle sicher mit TWINT, Kreditkarte oder weiteren Methoden',
  'how_it_works.step4_title': 'Erhalten',
  'how_it_works.step4_desc': 'Deine Bestellung wird innerhalb weniger Stunden geliefert',
  // Auth
  'auth.login_title': 'Anmelden',
  'auth.register_title': 'Registrieren',
  'auth.email': 'E-Mail',
  'auth.password': 'Passwort',
  'auth.forgot_password': 'Passwort vergessen?',
  'auth.no_account': 'Noch kein Konto?',
  'auth.has_account': 'Bereits ein Konto?',
  'auth.confirm_password': 'Passwort bestätigen',
  'auth.new_password': 'Neues Passwort',
  'auth.min_chars': 'Mindestens 6 Zeichen',
  'auth.repeat_password': 'Passwort wiederholen',
  'auth.change_password': 'Passwort ändern',
  'auth.changing_password': 'Wird geändert…',
  'auth.password_changed': 'Passwort erfolgreich geändert!',
  'auth.password_min_error': 'Passwort muss mindestens 6 Zeichen haben',
  'auth.password_mismatch': 'Passwörter stimmen nicht überein',
  'auth.confirmation_sent': 'Bestätigungs-E-Mail gesendet ✉️',
  'auth.confirmation_sent_desc': 'Wir haben dir eine E-Mail an <strong>{email}</strong> gesendet.',
  'auth.check_spam': 'Prüfe auch deinen <strong>Spam-Ordner</strong> und den <strong>Promotions-Tab</strong>.',
  'auth.link_not_received': 'Link nicht erhalten?',
  'auth.resend_in': 'Erneut senden in {seconds}s',
  'auth.resending': 'Wird gesendet…',
  'auth.resend': 'Erneut senden',
  'auth.resend_error': 'Fehler beim erneuten Senden',
  'auth.resend_success': 'Bestätigungsmail erneut gesendet!',
  'auth.forgot_title': 'Passwort vergessen?',
  'auth.forgot_desc': 'Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.',
  'auth.send_link': 'Link senden',
  'auth.sending': 'Wird gesendet…',
  'auth.email_sent': 'E-Mail gesendet ✉️',
  'auth.email_sent_desc': 'Falls ein Konto mit <strong>{email}</strong> existiert, haben wir dir einen Link zum Zurücksetzen deines Passworts gesendet.',
  'auth.back_to_login': 'Zurück zur Anmeldung',
  'auth.reset_title': 'Neues Passwort setzen',
  'auth.reset_desc': 'Wähle ein neues Passwort für dein Konto',
  'auth.invalid_link': 'Ungültiger Link',
  'auth.invalid_link_desc': 'Dieser Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen.',
  'auth.request_new_link': 'Neuen Link anfordern',
  'auth.saving': 'Wird gespeichert…',
  // Account
  'account.title': 'Mein Konto',
  'account.loading': 'Wird geladen…',
  'account.tab_profile': 'Profil',
  'account.tab_orders': 'Bestellungen',
  'account.tab_support': 'Support',
  'account.tab_addresses': 'Adressen',
  'account.tab_payment': 'Zahlung',
  'account.personal_data': 'Persönliche Daten',
  'account.personal_data_desc': 'Name und E-Mail verwalten',
  'account.display_name': 'Anzeigename',
  'account.display_name_placeholder': 'Dein Name',
  'account.profile_saved': 'Profil gespeichert!',
  'account.change_password': 'Passwort ändern',
  'account.change_password_desc': 'Setze ein neues Passwort für dein Konto',
  'account.my_orders': 'Meine Bestellungen',
  'account.orders_desc': 'Klicke auf eine Bestellung für Details',
  'account.no_orders': 'Du hast noch keine Bestellungen.',
  'account.order': 'Bestellung',
  'account.pay_now': 'Jetzt bezahlen',
  'account.saved_addresses': 'Gespeicherte Adressen',
  'account.addresses_desc': 'Deine Standardadresse wird beim Checkout vorausgefüllt',
  'account.add': 'Hinzufügen',
  'account.no_addresses': 'Noch keine Adresse gespeichert.',
  'account.default': 'Standard',
  'account.edit_address': 'Adresse bearbeiten',
  'account.new_address': 'Neue Adresse',
  'account.address_updated': 'Adresse aktualisiert!',
  'account.address_saved': 'Adresse gespeichert!',
  'account.address_deleted': 'Adresse gelöscht',
  'account.default_address_set': 'Standardadresse gesetzt',
  'account.first_name': 'Vorname',
  'account.last_name': 'Nachname',
  'account.street': 'Strasse & Nr.',
  'account.postal_code': 'PLZ',
  'account.city': 'Ort',
  'account.country': 'Land',
  'account.payment_methods': 'Zahlungsmethoden',
  'account.payment_methods_desc': 'Gespeicherte Karten und Zahlungsoptionen',
  'account.no_payment_methods': 'Noch keine Zahlungsmethode gespeichert.',
  'account.valid_until': 'Gültig bis',
  'account.add_payment': 'Zahlungsmethode hinzufügen',
  'account.label_optional': 'Bezeichnung (optional)',
  'account.label_placeholder': 'z.B. Persönliche Karte',
  'account.card_type': 'Kartentyp',
  'account.last_four': 'Letzte 4 Ziffern',
  'account.month': 'Monat',
  'account.year': 'Jahr',
  'account.payment_saved': 'Zahlungsmethode gespeichert!',
  'account.payment_deleted': 'Zahlungsmethode gelöscht',
  'account.default_payment_set': 'Standard-Zahlungsmethode gesetzt',
  // Order detail
  'order.back_to_account': 'Zurück zum Konto',
  'order.ordered_at': 'Bestellt am',
  'order.pay_now': 'Jetzt bezahlen',
  'order.items': 'Bestellte Artikel',
  'order.no_items': 'Keine Artikel gefunden.',
  'order.pieces': 'Stück',
  'order.target': 'Ziel',
  'order.copy': 'Kopieren',
  'order.copied': 'Kopiert!',
  'order.open': 'Öffnen',
  'order.delivery_status': 'Lieferstatus',
  'order.summary': 'Zusammenfassung',
  'order.subtotal': 'Zwischensumme',
  'order.discount': 'Rabatt',
  'order.vat': 'MwSt.',
  'order.total': 'Total',
  'order.details': 'Details',
  'order.country': 'Land',
  'order.address': 'Adresse',
  'order.payment': 'Zahlung',
  // Status labels
  'status.pending': 'Ausstehend',
  'status.processing': 'In Bearbeitung',
  'status.completed': 'Abgeschlossen',
  'status.failed': 'Fehlgeschlagen',
  'status.refunded': 'Erstattet',
  'status.cancelled': 'Storniert',
  'status.in_progress': 'In Bearbeitung',
  'status.partial': 'Teilweise',
  'status.error': 'Fehler',
  // Order success
  'order.success_title': 'Bestellung erfolgreich!',
  // Bestellabschluss-Seite (OrderSuccessPage) – vollstaendig ueber den
  // normalen Uebersetzungsweg, damit neue Storefronts nichts erben muessen.
  'success.page_title': 'Bestellung erfolgreich',
  'success.not_found': 'Bestellung nicht gefunden',
  'success.back_to_shop': 'Zurück zum Shop',
  'success.thanks': 'Vielen Dank für deine Bestellung! 🎉',
  'success.thanks_sub': 'Wir haben deine Bestellung erhalten und beginnen sofort mit der Bearbeitung.',
  'success.order_number': 'Bestellnummer',
  'success.date': 'Datum',
  'success.payment': 'Zahlung',
  'success.total': 'Total',
  'success.ordered_items': 'Bestellte Artikel',
  'success.target': 'Ziel',
  'success.subtotal': 'Zwischensumme',
  'success.discount': 'Rabatt',
  'success.vat': 'MwSt.',
  'success.delivery_title': 'Lieferzeit',
  'success.delivery_text': 'Deine Bestellung wird in der Regel innerhalb von 2–48 Stunden bearbeitet. Du erhältst eine Benachrichtigung per E-Mail an {email}.',
  'success.discount_card': 'Dein persönlicher Rabattcode!',
  'success.generating': 'Code wird generiert...',
  'success.thank_gift': 'Als Dankeschön schenken wir dir {percent}% Rabatt auf deine nächste Bestellung.',
  'success.copied': 'Kopiert!',
  'success.copy': 'Kopieren',
  'success.save_now': 'Speichere diesen Code jetzt! Wenn du diese Seite schliesst, ist der Code nicht mehr abrufbar.',
  'success.valid_for': '⏰ Gültig für {hours} Stunden · Einmalig einlösbar · 1 Bestellung',
  'success.code_error': 'Code konnte nicht generiert werden.',
  'success.continue': 'Weiter einkaufen',
  'success.code_copied': 'Code kopiert!',
  'success.agb': 'AGB',
  'success.privacy': 'Datenschutz',
  'success.impressum': 'Impressum',
  // Zahlungsarten: nur echte Woerter, Markennamen bleiben sprachneutral.
  'payment_method.card': 'Kreditkarte',
  'payment_method.sepa_debit': 'SEPA-Lastschrift',
  'payment_method.wallee': 'Online-Zahlung',
  'payment_method.default': 'Karte',
  'order.success_message': 'Vielen Dank für deine Bestellung. Du erhältst eine Bestätigung per E-Mail.',
  'order.number': 'Bestellnummer',
  // Home
  'home.hero_title': 'Follower kaufen mit TWINT',
  'home.hero_subtitle': 'Instagram & TikTok schnell steigern',
  'home.hero_text': 'Hochwertige, echte Follower sowie Likes, Views und Kommentare – in Minuten geliefert, bezahlt mit TWINT, Visa oder Mastercard, so einfach wie günstig – die besten Preise der Schweiz.',
  'home.cta': 'Jetzt starten',
  'home.services_title': 'Unsere Dienstleistungen',
  'home.fast_delivery': 'Rasche Lieferung',
  'home.fast_delivery_desc': 'Bestellung wird sofort abgewickelt. Resultate innerhalb von 2-48 Stunden.',
  'home.premium_quality': 'Premium Qualität',
  'home.premium_quality_desc': 'Wir setzen auf höchste Kundenzufriedenheit und garantieren die beste Qualität.',
  'home.secure_payment': 'Sichere Zahlung',
  'home.secure_payment_desc': 'Zahlung durch Twint, Visa oder Mastercard durch SSL Verschlüsselung geschützt.',
  'home.faq1_q': 'Warum sollte ich Followers kaufen?',
  'home.faq1_a': 'Je mehr Follower du hast, desto mehr Aufmerksamkeit wirst du von anderen Nutzern oder Unternehmen bekommen. Gute Inhalte sind der Schlüssel, um die Followerzahlen zu steigern.',
  'home.faq2_q': 'Sind die Followers echt?',
  'home.faq2_a': 'Ja, wir liefern ausschliesslich echte Follower von echten und aktiven Accounts. Alle diese Nutzer haben Profilbilder, Posts, Follower und folgen auch anderen Accounts.',
  'home.seo_title': 'Follower kaufen mit TWINT – Instagram & TikTok | BigFollow',
  'home.seo_description': 'Follower kaufen mit TWINT – Instagram & TikTok Follower, Likes, Views günstig kaufen. Beste Preise der Schweiz. Sofortige Lieferung.',
  // Blog
  'blog.page_title': 'Blog – Social Media Tipps & Tricks',
  'blog.page_description': 'Tipps, Tricks und Anleitungen rund um Instagram, TikTok, YouTube und Social Media Marketing. Von BigFollow – dem Schweizer Experten.',
  'blog.no_posts': 'Keine Blog-Beiträge vorhanden.',
  'blog.not_found': 'Beitrag nicht gefunden',
  'blog.back_to_blog': 'Zurück zum Blog',
  'blog.share': 'Teilen',
  'blog.copy_link': 'Link kopieren',
  'blog.link_copied': 'Link kopiert!',
  'blog.comments': 'Kommentare',
  'blog.leave_comment': 'Kommentar hinterlassen',
  'blog.submit_comment': 'Kommentar absenden',
  'blog.comment_approval_note': 'Dein Kommentar wird nach Freigabe veröffentlicht.',
  'blog.comment_submitted': 'Kommentar eingereicht! Er wird nach Freigabe angezeigt.',
  'blog.comment_too_long': 'Bitte kürze deinen Namen oder Kommentar.',
  'blog.author_bio': 'Bigfollow ist seit 2022 der führende Schweizer Anbieter für Social-Media-Wachstum, Follower-Pakete, Likes und Engagement-Boosts.',
  // Loyalty / Big-Punkte
  'loyalty.brand_name': 'Big-Punkte',
  'loyalty.earn_info_title': 'Sammle <accent>+{points} Big-Punkte</accent> mit diesem Kauf',
  'loyalty.earn_info_desc': 'Punkte sammeln & bei der nächsten Bestellung als Rabatt einlösen.',
  'loyalty.learn_more': 'Mehr erfahren',
  'loyalty.badge_title': 'Sammle {points} Big-Punkte mit dieser Bestellung',
  'loyalty.badge_label': '+{points} Big-Punkte',
  'loyalty.redeem_title': 'Big-Punkte einlösen',
  'loyalty.redeem_balance': 'Du hast {balance} Punkte (≈ CHF {chf})',
  'loyalty.redeem_remove': 'Entfernen',
  'loyalty.redeem_all': 'Alle',
  'loyalty.redeem_active': '{points} Punkte werden eingelöst',
  'loyalty.redeem_hint': 'Aktivieren um {points} Punkte einzulösen (−CHF {chf})',
  'loyalty.redeem_min_hint': 'Big-Punkte können ab {min} Punkten eingelöst werden. Aktueller Stand: {balance}.',
  'loyalty.row_label': 'Big-Punkte ({points})',
  'loyalty.dashboard_loading': 'Lade Big-Punkte…',
  'loyalty.how_it_works': 'Wie funktioniert das Big-Punkte System?',
  'loyalty.your_account': 'Dein Big-Punkte Konto',
  'loyalty.points_redeemable': '≈ CHF {chf} Rabatt einlösbar',
  'loyalty.points_to_next': 'Noch {points} Punkte bis {icon} {name}',
  'loyalty.lifetime_collected': 'Gesammelt insgesamt: {points} Punkte',
  'loyalty.max_level': '🎉 Du hast das höchste Level erreicht!',
  'loyalty.your_benefits': 'Deine Vorteile als {name}-Mitglied',
  'loyalty.bonus_per_order': '{percent}% Bonus auf jede Bestellung',
  'loyalty.recent_activity': 'Deine letzten Bewegungen',
  'loyalty.entries_count': '{count} Einträge',
  'loyalty.loading_activity': 'Lade Bewegungen…',
  'loyalty.no_activity': 'Noch keine Bewegungen. Sammle Punkte mit deiner nächsten Bestellung!',
  'loyalty.order_label': 'Bestellung #{number}',
  'loyalty.order_value_meta': 'Bestellwert CHF {chf}',
  'loyalty.earned_card_title': 'Big-Punkte gesammelt!',
  'loyalty.earned_card_chf': '≈ CHF {chf} Rabatt für die nächste Bestellung',
  'loyalty.earned_card_desc': 'Sammle bei jedem Kauf Punkte und löse sie beim Checkout als Rabatt ein. Je mehr du bestellst, desto höher dein Level & Bonus.',
  'loyalty.earned_card_link': 'Mehr über Big-Punkte erfahren',
  'loyalty.cta_login': 'Sammle Big-Punkte bei jeder Bestellung',
  'loyalty.cta_register': 'Sammle Big-Punkte ab der ersten Bestellung',
  'loyalty.points': 'Punkte',
  // Live-Chat-Widget (ChatWidget) – vollstaendig ueber den normalen
  // Uebersetzungsweg, damit Storefront Nr. 5 nichts hartkodiert erbt.
  'chat.title': 'BigFollow Support',
  'chat.online': 'Online',
  'chat.offline': 'Offline',
  'chat.intro_online': 'Starte einen Chat mit unserem Team.',
  'chat.intro_offline': 'Wir sind gerade offline. Hinterlasse uns eine Nachricht.',
  'chat.name_placeholder': 'Dein Name *',
  'chat.email_placeholder': 'E-Mail (optional)',
  'chat.start': 'Chat starten',
  'chat.empty': 'Schreibe eine Nachricht...',
  'chat.message_placeholder': 'Nachricht schreiben...',
  'chat.file': 'Datei',
  'chat.image': 'Bild',
  'chat.open_aria': 'Support-Chat öffnen',
  'chat.close_aria': 'Chat schliessen',
  // Hero-Profilkarte (Mockup) – Dekotexte, die wie Bilder wirken, aber
  // echter Text sind. Vollstaendig ueber den normalen Uebersetzungsweg.
  'hero.card.posts': 'Posts',
  'hero.card.followers': 'Follower',
  'hero.card.following': 'Folge ich',
  'hero.card.your_profile': 'Dein Profil',
  'hero.card.bio': 'Content Creator',
  'hero.card.follow': 'Folgen',
  'hero.card.message': 'Nachricht',
  // Produktseiten-Animationen ("So funktioniert's")
  'anim.payment_card': 'Kreditkarte',
  'anim.paid': 'Sicher bezahlt!',
  'anim.delivering': 'Wird geliefert...',
  'anim.completed': 'Abgeschlossen!',
  'anim.metric.followers': 'Follower',
  'anim.metric.likes': 'Likes',
  'anim.metric.views': 'Views',
  'anim.metric.comments': 'Kommentare',
  'anim.metric.shares': 'Shares',
  'anim.metric.subscribers': 'Abonnenten',
  'anim.metric.interactions': 'Interaktionen',
  // Paket-Vergleichstabelle (Instagram Wachstumspaket)
  'pkg.title': 'Das ist enthalten',
  'pkg.package': 'Paket',
  'pkg.row_followers': 'Super Real Follower',
  'pkg.row_likes': 'Super Real Likes',
  'pkg.row_comments': 'Echte Kommentare',
  'pkg.row_saves': 'Echte Speicherungen',
  'pkg.row_views': 'Echte Video-Views',
  'pkg.row_visits': 'Echte Profilbesuche',
  'pkg.badge_non_drop': 'Dauerhaft stabil',
  'pkg.badge_guarantee': 'Lebenslange Garantie',
  'pkg.note': 'Dauerhaft stabil · Lebenslange Garantie',
};

type TranslationCache = Record<string, string>;

let globalCache: Record<Locale, TranslationCache> = {
  de: DE_DEFAULTS,
  en: {},
  it: {},
  fr: {},
  sq: {},
};
let loaded: Record<Locale, boolean> = { de: true, en: false, it: false, fr: false, sq: false };

export function useTranslations() {
  const { locale } = useLanguage();
  const { storefront } = useStorefront();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || loaded[locale]) return;

    const fetchTranslations = async () => {
      const { data } = await supabase
        .from('ui_translations')
        .select('key, value')
        .eq('locale', locale);

      if (data) {
        const map: TranslationCache = {};
        for (const row of data) {
          map[row.key] = row.value;
        }
        globalCache[locale] = map;
      }
      loaded[locale] = true;
      forceUpdate(n => n + 1);
    };

    fetchTranslations();
  }, [locale]);

  const t = (key: string, fallback?: string): string => {
    const raw = locale === DEFAULT_LOCALE
      ? (DE_DEFAULTS[key] || fallback || key)
      : (globalCache[locale]?.[key] || DE_DEFAULTS[key] || fallback || key);
    // IT rewrite: only when the runtime storefront is 'it'.
    // CH (incl. /it/ second-locale) and AT unchanged — byte-identical.
    if (storefront === 'it') return rewriteForIT(raw);
    // AL rewrite: only on sf='al' and never on the legal routes.
    if (shouldScrubForAL(storefront)) return rewriteForAL(raw);
    return raw;
  };

  return { t, locale };
}
